import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/projects/NPS/Stepper/common/button";
import CommonSelect from "@/projects/NPS/Stepper/common/CommonSelect";
import { RadioGroup } from "@/projects/NPS/Stepper/common/RadioGroup";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import GetPensionFundManagerService from "@/services/NPS/Stepper/APIs/GetPensionFundManagerService";
import { PfmNameId } from "@/utils/types/NPS/Stepper/PensionFundManager";
import { Slider } from "@mui/material";
import InvestmentService from "@/services/NPS/Stepper/APIs/InvestmentService";
import BankDetailsService from "@/services/NPS/Stepper/APIs/BankDetailsService";
import GetRegistrationDetailsService from "@/services/NPS/Stepper/APIs/getRegistrationDetailsService";
import { BankDetailsPayload } from "@/utils/types/NPS/Stepper/BankDetils";
// ✅ Import your new service

// Define types
interface SchemeOption {
  value: string;
  label: string;
}

const InvestmentOptions: React.FC<{
  ackId: string;
  nextStep: string;
  initialDetails: any;
  onNext: () => void;
  onPrev: () => void;
  setNextStep: (nextStep: string) => void;
  setInitialDetails: (initialDetails: any) => void;
  registrationDetails?: Registration | null;
}> = ({
  ackId,
  onNext,
  onPrev,
  setNextStep,
  setInitialDetails,
  registrationDetails,
}) => {
  const [fundManager, setFundManager] = useState<string>("");
  const [fundManagerOptions, setFundManagerOptions] =
    useState<PfmNameId | null>(null);
  const [investmentChoice, setInvestmentChoice] = useState<
    "Balanced Life Cycle" | "Auto" | "Active"
  >("Balanced Life Cycle");

  const [autoChoice, setAutoChoice] = useState<
    "Aggressive" | "Moderate" | "Conservative"
  >("Moderate");

  // ✅ New: Store scheme options from API
  const [schemeOptions, setSchemeOptions] = useState<SchemeOption[]>([]);
  const [isLoadingSchemes, setIsLoadingSchemes] = useState(false);

  // ✅ State for allocations: keyed by scheme.value
  const [allocations, setAllocations] = useState<Record<string, number>>({});

  const autoPresets = useMemo(
    () => ({
      Aggressive: { equity: 75, corporate: 10, government: 15 },
      Moderate: { equity: 50, corporate: 30, government: 20 },
      Conservative: { equity: 25, corporate: 45, government: 30 },
    }),
    []
  );

  // ✅ Reset allocations to 0 when entering Active mode
  useEffect(() => {
    if (
      investmentChoice === "Active" &&
      registrationDetails?.schemeDtls?.[0]?.schPrefChoice !== "V" &&
      registrationDetails?.schemeDtls?.length > 0
    ) {
      const resetAllocations: Record<string, number> = {};
      schemeOptions.forEach(opt => {
        resetAllocations[opt.value] = 0;
      });
      setAllocations(resetAllocations);
    }
  }, [investmentChoice, schemeOptions, registrationDetails]);

  // ✅ Fetch schemes when fundManager changes
  const fetchSchemes = useCallback(async () => {
    setIsLoadingSchemes(true);
    try {
      const response =
        await GetPensionFundManagerService.getSchemeByPfmId(fundManager);
      if (response.success && response.options) {
        setSchemeOptions(response.options);

        const initAllocations: Record<string, number> = {};
        response.options.forEach((opt: any) => {
          if (!(opt.value in allocations)) {
            initAllocations[opt.value] = 0;
          } else {
            initAllocations[opt.value] = allocations[opt.value];
          }
        });

        setAllocations(prevAllocations => {
          const allocationsChanged = Object.keys(initAllocations).some(
            key => initAllocations[key] !== prevAllocations[key]
          );
          return allocationsChanged ? initAllocations : prevAllocations;
        });
      } else {
        setSchemeOptions([]);
        setAllocations({});
      }
    } catch (error) {
      console.error("Failed to fetch schemes", error);
      setSchemeOptions([]);
      setAllocations({});
    } finally {
      setIsLoadingSchemes(false);
    }
  }, [fundManager, allocations]); // Now including `allocations` in the dependency array

  useEffect(() => {
    if (fundManager) {
      fetchSchemes();
    } else {
      setSchemeOptions([]);
      setAllocations({});
    }
  }, [fundManager]);

  // No need to include `allocations` here, just listen to `fundManager`

  useEffect(() => {
    if (
      registrationDetails?.schemeDtls &&
      registrationDetails?.schemeDtls?.length > 0
    ) {
      const uniquePfmIds = new Set(
        registrationDetails?.schemeDtls[0]?.schemeList?.map(
          (item: any) => item.pfmId
        )
      );

      // Convert Set back to a comma-separated string
      const pfmIds = Array.from(uniquePfmIds).join(",");

      setFundManager(pfmIds);
      setInvestmentChoice(
        registrationDetails?.schemeDtls[0]?.schPrefChoice === "B"
          ? "Balanced Life Cycle"
          : registrationDetails?.schemeDtls[0]?.schPrefChoice === "A"
            ? "Auto"
            : "Active"
      );
      // setSchemeOptions(registrationDetails?.schemeDtls[0]?.schemeList);
      setAllocations(
        registrationDetails?.schemeDtls[0]?.schemeList?.reduce(
          (acc: Record<string, number>, item: any) => {
            acc[item.schemeId] = parseFloat(item.schemePercentage);
            return acc;
          },
          {}
        )
      );
    }
  }, [registrationDetails]);

  // Sync Auto mode allocations (only for preset labels)
  useEffect(() => {
    if (investmentChoice === "Auto") {
      const { equity, corporate, government } = autoPresets[autoChoice];
      setAllocations({
        equity,
        corporate,
        government,
      });
    }
  }, [investmentChoice, autoChoice, autoPresets]);

  const totalAllocation = useMemo(() => {
    if (investmentChoice !== "Active") {
      return 0;
    } // Irrelevant for other modes
    return Object.values(allocations).reduce((sum, val) => sum + val, 0);
  }, [allocations, investmentChoice]);

  useEffect(() => {
    if (investmentChoice === "Active") {
      setAllocations({});
    }
  }, [investmentChoice]);

  useEffect(() => {
    const fetchPensionFundManager = async () => {
      const response =
        await GetPensionFundManagerService.getPensionFundManager();
      if (response?.pfms?.pfmNameId) {
        setFundManagerOptions(response.pfms.pfmNameId);
        // ✅ Auto-select the first PFM
        const firstPfmId = Object.keys(response.pfms.pfmNameId)[0];
        if (
          firstPfmId &&
          !fundManager &&
          registrationDetails?.schemeDtls?.length === 0
        ) {
          setFundManager(firstPfmId);
        }
      }
    };
    fetchPensionFundManager();
  }, [fundManager, registrationDetails]);

  const handleContinue = async () => {
    let payload: any;
    if (investmentChoice === "Active") {
      // ✅ Build schemeList from allocations + schemeOptions
      // ✅ Filter to only include schemes with changed (non-zero) percentages
      const schemeList = schemeOptions
        .map(opt => ({
          pfmId: fundManager,
          schemeId: opt.value,
          schemePercentage: allocations[opt.value] || 0,
        }))
        .filter(scheme => scheme.schemePercentage > 0); // Only include schemes with changed values

      payload = {
        tier1: {
          schPrefChoice: "V",
          schemeList: schemeList,
        },
      };
    } else if (investmentChoice === "Auto") {
      let schPrefChoice = "A";
      if (autoChoice === "Aggressive") {
        schPrefChoice = "H";
      }
      if (autoChoice === "Conservative") {
        schPrefChoice = "L";
      }

      payload = {
        tier1: {
          schPrefChoice,
          schemeList: [
            {
              pfmId: fundManager,
            },
          ],
        },
      };
    } else {
      // Balanced
      payload = {
        tier1: {
          schPrefChoice: "B",
          schemeList: [
            {
              pfmId: fundManager,
            },
          ],
        },
      };
    }

    try {
      // ✅ Call the correct service — InvestmentService, not NomineeDetailsService
      const response = await InvestmentService.createInvestment(
        ackId,
        payload,
        "scheme"
      );

      if (response?.success) {
        // Calculate numberOfSchema based on the schemeList that was just saved
        let numberOfSchema = 1; // Default value

        if (investmentChoice === "Active" && payload.tier1.schemeList) {
          // For Active choice, use the actual schemeList length
          numberOfSchema = payload.tier1.schemeList.length;
        } else {
          // For Auto or Balanced, schemeList has 1 item (just pfmId)
          numberOfSchema = 1;
        }

        // Update bank details with the new numberOfSchema
        try {
          // Fetch current registration details to get bank details
          const registrationResponse =
            await GetRegistrationDetailsService.getRegistrationDetails();
          if (
            registrationResponse?.success &&
            registrationResponse?.registration
          ) {
            const currentRegistration = registrationResponse.registration;

            // Get bank details (handle both array and object structures)
            const bankDtlsArray = Array.isArray(currentRegistration?.bankDtls)
              ? currentRegistration.bankDtls
              : currentRegistration?.bankDtls?.tier1
                ? [currentRegistration.bankDtls]
                : [];

            if (bankDtlsArray.length > 0) {
              const bankDetails = bankDtlsArray[0];
              const bankTier1 = bankDetails?.tier1 ?? bankDetails;

              // Update bank details with new numberOfSchema
              const bankPayload: BankDetailsPayload = {
                tier1: {
                  accountType: bankTier1.accountType ?? "",
                  bankAccountNumber: bankTier1.bankAccountNumber ?? "",
                  bankIfsCode: bankTier1.bankIfsCode ?? "",
                  bankName: bankTier1.bankName ?? "",
                  linkedToAdhaarFlag: bankTier1.linkedToAdhaarFlag ?? "Y",
                  pennyDropVerf: bankTier1.pennyDropVerf ?? "Y",
                  numberOfNominee:
                    currentRegistration?.nominationDtls?.length ?? 1,
                  numberOfSchema: numberOfSchema,
                },
              };

              // Call bank API to update numberOfSchema
              await BankDetailsService.createBankDetails(
                ackId,
                bankPayload,
                "bank"
              );
            }
          }
        } catch (bankError) {
          console.error(
            "Error updating bank details after scheme submission:",
            bankError
          );
          // Continue even if bank update fails
        }

        setNextStep(response?.registration?.next_step); // Set next step
        setInitialDetails(response?.registration?.initialDetails);
        onNext(); // ✅ Proceed to next step
      } else {
        console.error("Investment submission failed:", response);
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  const fundManagerSelectOptions = Object.entries(fundManagerOptions ?? {}).map(
    ([value, label]) => ({ value, label })
  );

  // Slider styling
  const sliderSx = (isDisabled: boolean) => ({
    color: isDisabled ? "#999" : "#1AAE90",
    height: 6,
    "& .MuiSlider-thumb": {
      width: 16,
      height: 16,
      backgroundColor: isDisabled ? "#999" : "#333",
      "&:hover, &.Mui-focusVisible": {
        boxShadow: isDisabled ? "" : "0 0 0 8px rgba(26, 174, 144, 0.16)",
      },
    },
    "& .MuiSlider-track": { height: 6, borderRadius: 3 },
    "& .MuiSlider-rail": {
      height: 6,
      borderRadius: 3,
      backgroundColor: "#DFE7EC",
    },
    "& .MuiSlider-mark": { height: 0, width: 0 },
  });

  const renderSlider = (
    label: string,
    value: number,
    onChange: (val: number) => void,
    min: number,
    max: number,
    step: number,
    isDisabled: boolean
  ) => (
    <div className="w-full">
      <div className="flex items-center">
        <div className="w-[90px]">
          <span className="mb-2 flex items-center justify-center text-center font-['Nunito'] text-[14px] font-semibold leading-6 text-[#707070]">
            {label}
          </span>
          <span
            className={`ml-4 flex h-[55px] w-[60px] items-center justify-center gap-2.5 rounded-lg border px-[12px] py-[14px] ${
              isDisabled
                ? "border-[#B5B5B5] bg-[#B5B5B5]" // Disabled state styles
                : "border-[#DFE7EC] bg-[#E4FFFB]" // Active state styles
            } mr-[10px]`}
          >
            <span
              className={`text-center font-['Nunito'] text-[18px] font-bold leading-[30px] ${
                isDisabled
                  ? "text-[#FFFFFF]" // Disabled state styles
                  : "text-[#266985]" // Active state styles
              }`}
            >
              {value}%
            </span>
          </span>
        </div>
        <div className="w-[800px]">
          <Slider
            value={value}
            min={min}
            max={max}
            step={step}
            disabled={isDisabled}
            className="mt-14"
            onChange={(_, val) => onChange(val as number)}
            sx={sliderSx(isDisabled)}
          />
          <span className="flex justify-end text-base text-gray-500">
            {max}
          </span>
        </div>
      </div>
    </div>
  );

  // Helper: Get max for a scheme
  const getMaxForScheme = (label: string): number => {
    if (label?.toLowerCase().includes("equity")) {
      return 75;
    }
    if (label?.toLowerCase().includes("alternate")) {
      return 5;
    }
    return 100;
  };

  // Helper: Get step (always 0.5 for Active)
  const getStepForScheme = (): number => 0.5;

  return (
    <div className="space-y-10 rounded-2xl bg-white py-10">
      <h2 className="mb-4 font-nunito text-[20px] font-semibold text-[#1DAB91] sm:text-2xl md:text-[26px]">
        Investment Options
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
        <CommonSelect
          label="Pension Fund Manager"
          placeholder="Select Pension Fund Scheme"
          options={fundManagerSelectOptions}
          value={fundManager}
          onChange={val => setFundManager(val)}
        />
      </div>

      <div>
        <h3 className="mb-3 font-nunito text-[20px] font-semibold text-[#266985]">
          Investment Choice
        </h3>
        <RadioGroup
          options={[
            { key: "Balanced Life Cycle", label: "Balanced Life Cycle" },
            { key: "Auto", label: "Auto" },
            { key: "Active", label: "Active" },
          ]}
          value={investmentChoice}
          onChange={val =>
            setInvestmentChoice(
              val as "Balanced Life Cycle" | "Auto" | "Active"
            )
          }
        />

        {/* Sub-options for Auto */}
        {investmentChoice === "Auto" && (
          <div className="mt-4">
            <RadioGroup
              options={[
                { key: "Aggressive", label: "Aggressive Auto Choice (LC 75)" },
                { key: "Moderate", label: "Moderate Auto Choice (LC 50)" },
                {
                  key: "Conservative",
                  label: "Conservative Auto Choice (LC 25)",
                },
              ]}
              value={autoChoice}
              onChange={val => {
                setAutoChoice(
                  val as "Aggressive" | "Moderate" | "Conservative"
                );
                // ✅ Force investmentChoice to "Auto" if not already
                if (investmentChoice === "Auto") {
                  setInvestmentChoice("Auto");
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Show loader while fetching schemes */}
      {isLoadingSchemes && (
        <div className="text-center text-gray-500">Loading schemes...</div>
      )}

      <div className="mt-2 flex flex-col gap-4">
        {schemeOptions.length === 0 ? (
          <div className="py-4 text-center text-gray-500">
            No schemes available
          </div>
        ) : (
          <>
            {investmentChoice === "Balanced Life Cycle" && (
              <>
                {schemeOptions.map(opt => {
                  let fixedPerc = 0;
                  if (opt.label === "Equity") {
                    fixedPerc = 50;
                  } else if (opt.label === "Corporate") {
                    fixedPerc = 30;
                  } else if (opt.label === "Government") {
                    fixedPerc = 20;
                  }
                  // Alternate = 0

                  return renderSlider(
                    opt.label,
                    fixedPerc,
                    () => {},
                    0,
                    100,
                    1,
                    true
                  );
                })}
              </>
            )}

            {investmentChoice === "Auto" && (
              <>
                {schemeOptions.map(opt => {
                  let perc = 0;
                  const { equity, corporate, government } =
                    autoPresets[autoChoice];
                  if (opt.label === "Equity") {
                    perc = equity;
                  } else if (opt.label === "Corporate") {
                    perc = corporate;
                  } else if (opt.label === "Government") {
                    perc = government;
                  }
                  // Alternate = 0

                  return renderSlider(
                    opt.label,
                    perc,
                    () => {},
                    0,
                    100,
                    1,
                    true
                  );
                })}
              </>
            )}

            {/* Total Allocation Validator (only for Active) */}
            {investmentChoice === "Active" && (
              <div className="mt-1">
                <p
                  className={`text-sm font-semibold ${
                    totalAllocation === 100 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  Total allocation: {totalAllocation.toFixed(1)}% (
                  {totalAllocation === 100 ? "Valid" : "Must be exactly 100%"})
                </p>
              </div>
            )}

            {investmentChoice === "Active" && (
              <div>
                <h4 className="mb-3 font-nunito text-[18px] font-semibold text-[#000]">
                  Set Your Investment distribution for active choice
                </h4>
                {schemeOptions.map(opt => {
                  const value = allocations[opt.value] ?? 0;
                  const max = getMaxForScheme(opt.label);
                  return renderSlider(
                    opt.label,
                    value,
                    val =>
                      setAllocations(prev => ({ ...prev, [opt.value]: val })),
                    0,
                    max,
                    getStepForScheme(),
                    false
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Moderate Options Table (always shown) */}
        <div className="mt-6">
          <h5 className="font-['Nunito'] text-[20px] font-bold leading-[30px] text-[#2F363F] md:text-[24px]">
            Suggested investment options
          </h5>
          <div className="mt-[20px] flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8 md:gap-[60px]">
            <div>
              <div className="text-md mb-[10px] font-['Nunito'] font-medium leading-6 text-[#707070] md:text-lg">
                Equity
              </div>
              <div className="font-['Nunito'] text-lg font-bold leading-[30px] text-[#266985] md:text-xl">
                50%
              </div>
            </div>
            <div>
              <div className="mb-[10px] font-['Nunito'] text-lg font-medium leading-6 text-[#707070] md:text-xl">
                Corporates Bond
              </div>
              <div className="font-['Nunito'] text-lg font-bold leading-[30px] text-[#266985] md:text-xl">
                25%
              </div>
            </div>
            <div>
              <div className="mb-[10px] font-['Nunito'] text-lg font-medium leading-6 text-[#707070] md:text-xl">
                Government Bond
              </div>
              <div className="font-['Nunito'] text-lg font-bold leading-[30px] text-[#266985] md:text-xl">
                25%
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-center gap-3 sm:justify-end md:gap-5">
        <Button
          type="button"
          onClick={() => {
            // setBackFromStep("scheme");
            onPrev();
          }}
          className="h-[50px] rounded-md bg-[#F5F5F5] px-8 py-2 font-nunito text-[18px] font-semibold text-[#2F363F] hover:bg-[#f5f5f5] hover:opacity-95"
        >
          Back
        </Button>
        <Button
          type="submit"
          onClick={handleContinue}
          className="h-[50px] rounded-md bg-gradient-to-r from-[#127B8D] to-[#10B981] px-6 py-2 font-nunito text-[18px] font-semibold text-white transition hover:opacity-95"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default InvestmentOptions;
